'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import { EntityTag } from '@/components/search/entity-tag';
import { RowAside, TickerText } from '@/components/search/search-row-parts';
import { tickerLabel } from '@/components/search/search-row-parts.utils';
import { SearchRowSubline } from '@/components/search/search-row-subline';
import type { WinnerSearchResult } from '@/components/search/search-types';
import { useCommandLink } from '@/components/search/use-command-link';
import { CommandItem } from '@/components/ui/app-command';
import { ValueChangeArrow } from '@/components/ui/value-change-arrow';
import { useFormattedCurrency } from '@/lib/currency';
import { formatPercentValue } from '@/lib/format/format-number';
import { cn } from '@/lib/ui/utils';
interface WinnerRowProps {
  winner: WinnerSearchResult;
  href: string;
  tickerHighlighted?: boolean;
  onCommit: () => void;
  onHoverPrefetch?: (marketPubkey: string) => void;
}
function nonEmpty(value: string | undefined): string | null {
  if (value === undefined || value === '') return null;
  return value;
}
function shouldShowNumeric(value: string | undefined): boolean {
  if (value === undefined || value === '' || value === '0') return false;
  try {
    return BigInt(value) > BigInt(0);
  } catch {
    // SILENT-CATCH: non-numeric strings collapse the metric fragment
    return false;
  }
}

/** Signed-bps 24h change as a circle-arrow + percentage fragment. `null`/empty/zero → null. */
function changeDelta(bps: string | null, locale: string): React.ReactNode | null {
  if (bps === null || bps === '') return null;
  const pct = Number(bps) / 100;
  if (!Number.isFinite(pct)) return null;
  const abs = Math.abs(pct);
  if (Math.round(abs * 10) === 0) return null;
  const isUp = pct > 0;
  const label = `${formatPercentValue(abs, locale)}%`;
  return <span className={cn('inline-flex items-center gap-1 font-medium tabular-nums', isUp ? 'text-data-profit' : 'text-data-loss')}>
      <ValueChangeArrow direction={isUp ? 'up' : 'down'} className="size-3" />
      {label}
    </span>;
}

/**
 * A Winners-lane search result row — a graduated market's WINNING outcome,
 * trading as a real Solana token. Subline: `$ticker · $price · $Y traded · Δ`;
 * the AMM market cap is the primary right-hand metric.
 */
export function WinnerRow(t0) {
  const $ = _c(54);
  const {
    winner,
    href,
    tickerHighlighted,
    onCommit,
    onHoverPrefetch
  } = t0;
  const locale = useLocale();
  const t = useTranslations("search");
  const tMarketRow = useTranslations("market.row");
  const {
    formatD6
  } = useFormattedCurrency();
  const {
    onSelect,
    overlay
  } = useCommandLink(href, onCommit);
  let t1;
  if ($[0] !== onHoverPrefetch || $[1] !== winner.market) {
    t1 = () => {
      onHoverPrefetch?.(winner.market);
    };
    $[0] = onHoverPrefetch;
    $[1] = winner.market;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleHover = t1;
  let t2;
  if ($[3] !== winner.coverImageUrl) {
    t2 = nonEmpty(winner.coverImageUrl);
    $[3] = winner.coverImageUrl;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const usingCover = t2 !== null;
  let t3;
  if ($[5] !== usingCover || $[6] !== winner.coverImageUrl || $[7] !== winner.marketImageUrl) {
    t3 = usingCover ? winner.coverImageUrl : nonEmpty(winner.marketImageUrl);
    $[5] = usingCover;
    $[6] = winner.coverImageUrl;
    $[7] = winner.marketImageUrl;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const imageSrc = t3;
  const imageNsfw = usingCover ? winner.coverImageNsfw ?? false : winner.marketImageNsfw ?? false;
  let t4;
  if ($[9] !== formatD6 || $[10] !== winner.price) {
    t4 = shouldShowNumeric(winner.price) ? formatD6(winner.price, "compact") : null;
    $[9] = formatD6;
    $[10] = winner.price;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const pricePiece = t4;
  let t5;
  if ($[12] !== formatD6 || $[13] !== tMarketRow || $[14] !== winner.volumeTotal) {
    t5 = shouldShowNumeric(winner.volumeTotal) ? tMarketRow("traded", {
      value: formatD6(winner.volumeTotal, "compact")
    }) : null;
    $[12] = formatD6;
    $[13] = tMarketRow;
    $[14] = winner.volumeTotal;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const tradedPiece = t5;
  const t6 = winner.pubkey;
  let t7;
  if ($[16] !== imageNsfw || $[17] !== imageSrc || $[18] !== winner.market) {
    t7 = <OptimizedImage variant="thumbnail" size="size-8" entity={{
      kind: "market",
      pubkey: winner.market,
      category: "",
      coverImageUrl: imageSrc,
      nsfw: imageNsfw
    }} alt="" preview={false} />;
    $[16] = imageNsfw;
    $[17] = imageSrc;
    $[18] = winner.market;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t7 || $[21] !== winner.marketType) {
    t8 = <ThumbWithOrigin marketType={winner.marketType} size="sm">{t7}</ThumbWithOrigin>;
    $[20] = t7;
    $[21] = winner.marketType;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== winner.label) {
    t9 = <span className="truncate text-body font-medium">{winner.label}</span>;
    $[23] = winner.label;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <EntityTag kind="winner" />;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t9) {
    t11 = <div className="flex min-w-0 items-center gap-2">{t9}{t10}</div>;
    $[26] = t9;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== tickerHighlighted || $[29] !== winner.ticker) {
    t12 = tickerLabel(winner.ticker) !== null ? <TickerText key="ticker" ticker={winner.ticker} highlighted={tickerHighlighted} /> : null;
    $[28] = tickerHighlighted;
    $[29] = winner.ticker;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== locale || $[32] !== winner.change24hBps) {
    t13 = changeDelta(winner.change24hBps, locale);
    $[31] = locale;
    $[32] = winner.change24hBps;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== pricePiece || $[35] !== t12 || $[36] !== t13 || $[37] !== tradedPiece) {
    t14 = <SearchRowSubline pieces={[t12, pricePiece, tradedPiece, t13]} />;
    $[34] = pricePiece;
    $[35] = t12;
    $[36] = t13;
    $[37] = tradedPiece;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t11 || $[40] !== t14) {
    t15 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t11}{t14}</div>;
    $[39] = t11;
    $[40] = t14;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== formatD6 || $[43] !== t || $[44] !== winner.marketCapD6) {
    t16 = shouldShowNumeric(winner.marketCapD6) ? <RowAside value={formatD6(winner.marketCapD6, "compact")} label={t("label_mkt_cap_short")} /> : null;
    $[42] = formatD6;
    $[43] = t;
    $[44] = winner.marketCapD6;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  let t17;
  if ($[46] !== handleHover || $[47] !== onSelect || $[48] !== overlay || $[49] !== t15 || $[50] !== t16 || $[51] !== t8 || $[52] !== winner.pubkey) {
    t17 = <CommandItem value={t6} onSelect={onSelect} onMouseEnter={handleHover} onFocus={handleHover} className="relative gap-3 rounded-overlay-item px-3 py-2.5">{overlay}{t8}{t15}{t16}</CommandItem>;
    $[46] = handleHover;
    $[47] = onSelect;
    $[48] = overlay;
    $[49] = t15;
    $[50] = t16;
    $[51] = t8;
    $[52] = winner.pubkey;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  return t17;
}